import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subscription, Subject } from 'rxjs';

declare var $:any;

@Injectable()
export class ToolsService {

  // private variable to store if any note is in editing mode at present or not.
  // true = atleast 1 note is active.
  private isNoteActive = false;
  private notesReferenceMap = {};
  private singleNoteReference = undefined;
  private sidebarConfig:any = {};
  private activeNoteReference = undefined;
  private selectedNoteId = undefined;

  private sidebarConfigChangeNotifier = new Subject<any>();
  sidebarConfigChangeObservable$ = this.sidebarConfigChangeNotifier.asObservable();

  private toolbarConfigUpdateNotifier = new Subject<any>();
  toolbarConfigUpdateObservable$ = this.toolbarConfigUpdateNotifier.asObservable();

  private focContainerClickEventData = new Subject<any>();
  focContainerClickEventDataObservable$ = this.focContainerClickEventData.asObservable();

  constructor(private http: HttpClient) { }

  public getToolbarConfig() {
    let toolbarConfig = {
          "current-menu" : null, // null, "MAIN_MENU", "PEN_MENU", "HIGHLIGHTER_MENU", "PAGE_VIEW"
          "current-sub-menu" : null, // null, "PEN_SUB_MENU_COLOR", "PEN_SUB_MENU_WIDTH", "HIGHLIGHTER_SUB_MENU_COLOR", "HIGHLIGHTER_SUB_MENU_WIDTH", "PAGE_VIEW_ZOOM"
          "position" : "left", //  "left", "right"
          "showTools" : false, // true || false
          "showDrawingMenu" : false, // true || false 
          "currentTool" : undefined, // pen || highlighter || delete
          "showSelectionBoxMenu" : false, // true || false
          "tocActive" : false, // true || false
          "previousPageLabel" : "", // store the last jump to page page number
          "currentPageLabel" : "", // store the current page number
          "lastActiveFeature": undefined, // store the last active feature if it was spotlight or mask
          "notesWrapperActive": false, // true || false
          "sidebarActive": false, // true || false (store if either of toc or notes container is active) 
          "toolbarStatusType" : "3", // to store what type of message is to be shown as status. (use codes) 1: saving-failed, 2: saving, 3:saved
          "isStatusActive" : false, // to store if status button is active or not
          "isAssignmentModalActive" : false,
          "nextPage" : {
            "disabled": false
          },
          "previousPage" : {
            "disabled": false
          },
          "menus": {
            "MAIN_MENU": {
              "menu-items": {
                "sidebar": {
                  "visible": true
                },
                "pen": {
                  "visible": true
                },
                "highlighter": {
                  "visible": true
                },
                "page-view": {
                  "visible": true
                },
                "delete": {
                  "visible": true,
                  "disabled": true
                },
                "timer": {
                  "visible": true
                }
              }
            },
            "PEN_MENU": {
              "menu-items": {
                "previous-menu": {
                  "visible": true
                },
                "color": {
                  "visible": true,
                  "stroke-color": ["#000000", "#0002FF", "#B90000", "#00B32E"]
                },
                "width": {
                  "visible": true,
                  "stroke-size": ["2px", "6px", "10px", "14px"],
                  "stroke-size-svg-name": ['line-weight-1', 'line-weight-2', 'line-weight-3', 'line-weight-4']
                },
                "undo": {
                  "visible": true,
                  "disabled" : true
                },
                "redo": {
                  "visible": true,
                  "disabled" : true
                },
                "hide": {
                  "visible": true,
                  "disabled": false
                }
          },
          "value": {
            "stroke-color": "#000000",
            "stroke-size": "6px",
            "stroke-size-svg-name": "line-weight-2"
          }
        },
        "HIGHLIGHTER_MENU": {
          "menu-items": {
            "previous-menu": {
              "visible": true
            },
            "color": {
              "visible": true,
              "stroke-color": ["#FFFF00", "#75F8F8", "#F878F8", "#78F878"],
            },
            "width": {
              "visible": true,
              "stroke-size": ["4px", "16px", "20px", "24px"],
              "stroke-size-svg-name": ['line-weight-1', 'line-weight-2', 'line-weight-3', 'line-weight-4']
            },
            "undo": {
              "visible": true,
              "disabled": true
            },
            "redo": {
              "visible": true,
              "disabled": true
            },
            "hide": {
              "visible": true,
              "disabled": false
            }
          },
          "value": {
            "stroke-size": "16px",
            "stroke-color": "#FFFF00",
            "stroke-size-svg-name": "line-weight-2"
          }
        },
        "PAGE_VIEW": {
          "menu-items": {
            "previous-menu": {
              "visible": true
            },
            "zoom": {
              "visible": true,
              "zoomInDisabled": false,
              "zoomOutDisabled": true
            },
            "fit-to-width": {
              "visible": true
            },
            "fit-to-screen": {
              "visible": true,
              "disabled": true
            },
            "single-double-page-view": {
              "visible": true,
              "disabled": false,
              "currentPageLayout": "double"
            },
            "spotlight": {
              "visible": true,
              "disabled": true
            },
            "mask": {
              "visible": true,
              "disabled": true
            }
          }
        }
          }
        };
        return toolbarConfig;
    }

    public getTimerConfig() {
      let timerConfig = {
        "launch-mode" : "down",
        "auto-play" : false,
        "count-down" : {
            "time" : 0
        }
      };
      return timerConfig
    }
    
  public getSidebarConfig() {
    let sidebarConfig = {
      "isActive": false,
      "position": undefined,
      "current-sidebar": undefined // "toc"/"notes"
    };
    return sidebarConfig
  }

  public getSidebarConfigUpdated() {
    return this.sidebarConfig;
  }

  public setSidebarConfig(config) {
    this.sidebarConfig = config;
    this.sidebarConfigChangeNotifier.next(this.sidebarConfig);
  }

  // function to get the status.
  public getNoteStatus() {
    return this.isNoteActive;
  }

  // function to set the status.
  public setNoteStatus(status) {
    this.isNoteActive = status;
  }

  public setActiveNoteReference(noteReference) {
    this.activeNoteReference = noteReference;
  }

  public getActiveNoteReference() {
    return this.activeNoteReference;
  }

  public addNoteToNotesMap(noteData, reference) {
    this.notesReferenceMap[noteData.id] = reference;
  }

  public getNotefromNotesMap(id) {
    return this.notesReferenceMap[id];
  }

  public removeNotefromNotesMap(id) {
    delete this.notesReferenceMap[id];
  }

  public setSingleNoteReference(reference) {
    this.singleNoteReference = reference;
  }
  
  public getSingleNoteReference() {
    return this.singleNoteReference;
  }

  public removeSingleNoteReference() {
    this.singleNoteReference = undefined;
  }

  public updateToolbarConfig(keyValueObject) {
    this.toolbarConfigUpdateNotifier.next(keyValueObject);
  }

  public setSelectedNoteId(noteId) {
    this.selectedNoteId = noteId;
  }

  public getSelectedNoteId() {
    return this.selectedNoteId;
  }

  public removeSelectedNoteId() {
    this.selectedNoteId = undefined;
  }

  public getNoteReferenceMap() {
    return this.notesReferenceMap;
  }

  public updateFocContainerClickEventData(eventData) {
    return this.focContainerClickEventData.next(eventData);
  }

  public reset(){
    this.notesReferenceMap = {};
    this.singleNoteReference = undefined;
    this.sidebarConfig = {};
    this.activeNoteReference = undefined;
  }
    
}

   